/**
 * Phone SVG component — a phone displaying a QR code on its screen.
 * Used by the kiosk QR-scan animation (KioskSwipeInstructionSVG).
 */
import React from "react";
import { OUTER_D, PIN_D, VB } from "./pinpointLogoPaths";

export const PHONE_W = 64;
export const PHONE_H = 128;

const QR_SIZE = 15; // modules per side (>= 15 keeps the three 7x7 finder squares from overlapping)
export const QR_BOX = 42; // rendered px size of the QR square
const MOD = QR_BOX / QR_SIZE;
export const QR_X = (PHONE_W - QR_BOX) / 2;
export const QR_Y = 35; // leaves room for the Pinpoint badge above it
// Outer edge of the QR's white quiet-zone border (see the white backing rect below) —
// the bounds a scan-line overlay should sweep across.
export const QR_QUIET = 3;

// Fixed "finder pattern" corner squares (top-left, top-right, bottom-left),
// drawn as nested 7x7 / 5x5 / 3x3 rings — the classic QR eye markers.
const FINDER_ORIGINS = [
  { col: 0, row: 0 },
  { col: QR_SIZE - 7, row: 0 },
  { col: 0, row: QR_SIZE - 7 },
];

function isInsideFinder(col, row) {
  return FINDER_ORIGINS.some(
    (f) => col >= f.col && col < f.col + 7 && row >= f.row && row < f.row + 7
  );
}

function Finder({ col, row }) {
  const x = QR_X + col * MOD;
  const y = QR_Y + row * MOD;
  return (
    <g>
      <rect x={x} y={y} width={MOD * 7} height={MOD * 7} fill="#111" />
      <rect x={x + MOD} y={y + MOD} width={MOD * 5} height={MOD * 5} fill="#fff" />
      <rect x={x + MOD * 2} y={y + MOD * 2} width={MOD * 3} height={MOD * 3} fill="#111" />
    </g>
  );
}

// Deterministic "data module" noise — not a decodable QR, just a
// recognizable pattern. Skips cells occupied by a finder pattern.
const DATA_MODULES = [];
for (let row = 0; row < QR_SIZE; row++) {
  for (let col = 0; col < QR_SIZE; col++) {
    if (isInsideFinder(col, row)) continue;
    if ((col * 5 + row * 3 + col * row) % 3 === 0) {
      DATA_MODULES.push({ col, row });
    }
  }
}

// Small Pinpoint logo badge, top-center of the screen above the QR code —
// the same "pin" mark used by KioskLoadingAnimation, on a white plate so
// it reads clearly against the phone's screen.
function PinpointBadge({ cx, cy, uid = "" }) {
  const gradOuterId = uid ? `phone-badge-outer-${uid}` : "phone-badge-outer";
  const gradPinId = uid ? `phone-badge-pin-${uid}` : "phone-badge-pin";
  const plateR = 7;
  const iconSize = 13; // rendered size, in px, of the 512x512 source icon
  const s = iconSize / VB;

  return (
    <g>
      <defs>
        <linearGradient id={gradOuterId} x1="131.26" y1="172.25" x2="380.74" y2="172.25" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#EC2227" />
          <stop offset="1" stopColor="#F15B27" />
        </linearGradient>
        <linearGradient id={gradPinId} x1="131.88" y1="380.34" x2="241.58" y2="380.34" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#EC2227" />
          <stop offset="1" stopColor="#F15B27" />
        </linearGradient>
      </defs>
      <circle cx={cx} cy={cy} r={plateR} fill="#fff" stroke="#eaeaea" strokeWidth={0.5} />
      <g transform={`translate(${cx}, ${cy}) scale(${s}) translate(${-VB / 2}, ${-VB / 2})`}>
        <path d={OUTER_D} fill={`url(#${gradOuterId})`} />
        <path d={PIN_D} fill={`url(#${gradPinId})`} />
      </g>
    </g>
  );
}

export const PhoneWithQr = React.memo(function PhoneWithQr({ uid = "" }) {
  const bezel = 5;
  const screenX = bezel;
  const screenY = bezel + 6;
  const screenW = PHONE_W - bezel * 2;
  const screenH = PHONE_H - bezel * 2 - 12;
  const badgeCy = screenY + 11; // top-center of the screen, above the QR code

  return (
    <g>
      {/* Body */}
      <rect width={PHONE_W} height={PHONE_H} rx={11} fill="#1a1a1a" stroke="#333" strokeWidth={0.5} />
      {/* Screen */}
      <rect x={screenX} y={screenY} width={screenW} height={screenH} rx={4} fill="#fafafa" />
      {/* Front camera */}
      <circle cx={PHONE_W / 2} cy={bezel + 3} r={1.4} fill="#333" />
      {/* Pinpoint badge */}
      <PinpointBadge cx={PHONE_W / 2} cy={badgeCy} uid={uid} />
      {/* QR code */}
      <g>
        <rect
          x={QR_X - QR_QUIET}
          y={QR_Y - QR_QUIET}
          width={QR_BOX + QR_QUIET * 2}
          height={QR_BOX + QR_QUIET * 2}
          fill="#fff"
        />
        {FINDER_ORIGINS.map((f, i) => (
          <Finder key={i} col={f.col} row={f.row} />
        ))}
        {DATA_MODULES.map((m, i) => (
          <rect
            key={i}
            x={QR_X + m.col * MOD}
            y={QR_Y + m.row * MOD}
            width={MOD}
            height={MOD}
            fill="#111"
          />
        ))}
      </g>
      {/* Label */}
      <text
        x={PHONE_W / 2}
        y={QR_Y + QR_BOX + 14}
        fill="#555"
        fontSize={6}
        fontWeight="600"
        textAnchor="middle"
        fontFamily="'Helvetica Neue',Helvetica,Arial,sans-serif"
      >
        Check-In QR
      </text>
      {/* Home indicator */}
      <rect
        x={PHONE_W / 2 - 10}
        y={PHONE_H - bezel - 3}
        width={20}
        height={2}
        rx={1}
        fill="#555"
      />
    </g>
  );
});
